import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { onboardingActivityKeySchema } from '@entities/education-activity';
import { StudentEducationActivityPage } from '@pages/student';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessPersonalFlow } from '@shared/lib';

export const onboardingActivitySearchSchema = z.object({
  mode: z.enum(['resume', 'review']).catch('resume'),
  slideId: z.string().min(1).optional().catch(undefined),
});

export function enforceOnboardingEducationActivityRouteAccess({
  context,
  params,
}: {
  context: RouterContext;
  params: { locale: string; activityKey: string };
}): void {
  if (context.auth.isLoading) return;

  const hasMenuAccess = canAccessPersonalFlow(context.ability, context.auth.user?.id ?? null);
  if (hasMenuAccess && onboardingActivityKeySchema.safeParse(params.activityKey).success) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({
    to: '/$locale/unauthorized',
    params: { locale: params.locale },
  });
}

export const Route = createFileRoute(
  '/$locale/_authenticated/student/onboarding/activities/$activityKey'
)({
  component: OnboardingEducationActivityRoute,
  validateSearch: onboardingActivitySearchSchema,
  beforeLoad: ({ context, params }) => {
    enforceOnboardingEducationActivityRouteAccess({
      context: context as RouterContext,
      params,
    });
  },
});

function OnboardingEducationActivityRoute() {
  const { locale, activityKey } = Route.useParams();
  const { mode, slideId } = Route.useSearch();

  return (
    <StudentEducationActivityPage
      locale={locale}
      context="onboarding"
      activityKey={activityKey}
      mode={mode}
      slideId={slideId}
    />
  );
}
